import React, { useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    MenuItem,
    TextField,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import {
    ExpandMore,
    ChevronRight,
    Description as DocumentIcon,
} from '@material-ui/icons'

import { useDocuments } from '../../hooks/useDocuments'
import { ResidentTerm, LeaseDocument, CompletionStatus } from '../../types'
import { ResidentTermDocumentsTable } from './ResidentTermDocumentsTable'
import { UnpairedSearchDocumentsTable } from './UnpairedSearchDocumentsTable'
import { DocumentViewer } from './DocumentViewer'
import { useResidentTermListViewStyles } from './styles'

interface ResidentTermListViewProps {
    auditId: number
    allResidentTerms: ResidentTerm[]
    filteredTerms: ResidentTerm[]
    isLoadingResidentTerms: boolean
    isFetchingDocuments: boolean
    fetchResidentTermDocuments: (params: { residentTermId: number }) => void
    completionFilter: CompletionFilter
    residentSearchQuery: string
}

type CompletionFilter = 'all' | 'complete' | 'reviewed' | 'needs_review'

export const ResidentTermListView: React.FC<ResidentTermListViewProps> = ({
    auditId,
    allResidentTerms,
    filteredTerms,
    isLoadingResidentTerms,
    isFetchingDocuments,
    fetchResidentTermDocuments,
    completionFilter,
    residentSearchQuery,
}) => {
    const classes = useResidentTermListViewStyles()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(25)
    const [expandedAccordion, setExpandedAccordion] = useState<number | false>(
        false,
    )
    const [viewingDocument, setViewingDocument] = useState<{
        id: number
        name: string
        file?: string
    } | null>(null)
    // Client-side pagination
    const paginatedTerms = useMemo(() => {
        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize
        return filteredTerms.slice(startIndex, endIndex)
    }, [filteredTerms, page, pageSize])

    const totalPages = Math.ceil(filteredTerms.length / pageSize)

    const {
        pairDocument,
        unpairDocument,
        selectDocument,
        searchForDocuments,
        searchedDocuments,
        isSearchingDocuments,
    } = useDocuments({
        auditId,
        paired: false,
    })

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value)
    }

    const handlePageSizeChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value))
    }

    // Reset to page 1 when filter changes
    React.useEffect(() => {
        setPage(1)
    }, [allResidentTerms, completionFilter, residentSearchQuery])

    const handleAccordionChange = (residentTermId: number) => (
        _: React.ChangeEvent<{}>,
        isExpanded: boolean,
    ) => {
        const previouslyExpanded = expandedAccordion === residentTermId
        setExpandedAccordion(isExpanded ? residentTermId : false)

        if (isExpanded) {
            // Fetch paired documents AND search for unpaired documents
            fetchResidentTermDocuments({ residentTermId })
            searchForDocuments({ residentTermId })
        }
    }

    const handlePairDocument = (documentId: number) => {
        if (expandedAccordion) {
            pairDocument(
                {
                    documentId,
                    residentTermId: expandedAccordion as number,
                },
                {
                    onSuccess: () => {
                        // Refetch both paired and unpaired documents lists
                        fetchResidentTermDocuments({
                            residentTermId: expandedAccordion as number,
                        })
                        searchForDocuments({
                            residentTermId: expandedAccordion as number,
                        })
                    },
                },
            )
        }
    }

    const handleUnpairDocument = (documentId: number) => {
        if (expandedAccordion) {
            unpairDocument(
                { documentId },
                {
                    onSuccess: () => {
                        // Refetch both paired and unpaired documents lists
                        fetchResidentTermDocuments({
                            residentTermId: expandedAccordion as number,
                        })
                        searchForDocuments({
                            residentTermId: expandedAccordion as number,
                        })
                    },
                },
            )
        }
    }

    const handleViewDocument = (doc: LeaseDocument) => {
        setViewingDocument({
            id: doc.id,
            name: doc.file_name,
            file: doc.file, // S3 URL from the file field
        })
    }

    const handleViewerClose = () => {
        setViewingDocument(null)
    }

    const getCompletionStatus = (term: ResidentTerm): CompletionStatus => {
        // If backend provides completion_status, use it
        if (term.completion_status) {
            return term.completion_status
        }

        // New logic
        const hasSelected = term.has_selected_document ?? false
        const reviewed = term.reviewed ?? false

        if (hasSelected) {
            return 'complete'
        } else if (reviewed) {
            return 'reviewed'
        } else {
            return 'needs_review'
        }
    }

    const getStatusIcon = (status: CompletionStatus) => {
        switch (status) {
            case 'complete':
                return '✓'
            case 'reviewed':
                return '👁'
            case 'needs_review':
            case 'missing':
                return '⚠'
        }
    }

    const getStatusClass = (status: CompletionStatus) => {
        switch (status) {
            case 'complete':
                return classes.statusComplete
            case 'reviewed':
                return classes.statusReviewed
            case 'needs_review':
            case 'missing':
                return classes.statusNeedsReview
        }
    }

    if (isLoadingResidentTerms) {
        return (
            <Box className={classes.loadingState}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            {/* Resident Terms Accordion List */}
            <Box className={classes.accordionContainer}>
                {paginatedTerms.map((residentTerm) => (
                    <Accordion
                        key={residentTerm.id}
                        expanded={expandedAccordion === residentTerm.id}
                        onChange={handleAccordionChange(residentTerm.id)}
                        className={classes.accordion}
                    >
                        <AccordionSummary
                            expandIcon={
                                expandedAccordion === residentTerm.id ? (
                                    <ExpandMore />
                                ) : (
                                    <ChevronRight />
                                )
                            }
                            className={classes.accordionSummary}
                        >
                            <Box className={classes.residentInfo}>
                                <Box
                                    className={`${
                                        classes.statusIndicator
                                    } ${getStatusClass(
                                        getCompletionStatus(residentTerm),
                                    )}`}
                                    title={getCompletionStatus(residentTerm)}
                                >
                                    {getStatusIcon(
                                        getCompletionStatus(residentTerm),
                                    )}
                                </Box>
                                <Typography className={classes.residentName}>
                                    {residentTerm.resident}
                                </Typography>
                                <Typography className={classes.buildingUnit}>
                                    Unit {residentTerm.bldg_unit}
                                </Typography>
                                <Typography className={classes.leaseDates}>
                                    {residentTerm.lease_start} -{' '}
                                    {residentTerm.lease_end}
                                </Typography>
                                <Box className={classes.documentCount}>
                                    <DocumentIcon
                                        fontSize="small"
                                        color="action"
                                    />
                                    <Typography
                                        className={classes.documentCountText}
                                    >
                                        {residentTerm.document_count ?? 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box style={{ width: '100%' }}>
                                {/* Paired Documents Section */}
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Paired Documents
                                    </Typography>
                                    <ResidentTermDocumentsTable
                                        residentTerm={residentTerm}
                                        onViewDocument={handleViewDocument}
                                        onUnpairDocument={handleUnpairDocument}
                                        onSelectDocument={(documentId) =>
                                            selectDocument({
                                                documentId,
                                            })
                                        }
                                        isLoading={
                                            isFetchingDocuments &&
                                            expandedAccordion ===
                                                residentTerm.id
                                        }
                                        hideSearchButton={true}
                                    />
                                </Box>

                                {/* Unpaired Documents Section */}
                                {expandedAccordion === residentTerm.id && (
                                    <Box style={{ marginTop: 24 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Documents similar to this Resident
                                            term
                                        </Typography>
                                        {isSearchingDocuments ? (
                                            <Box
                                                style={{
                                                    padding: 16,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <CircularProgress size={24} />
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Searching for documents...
                                                </Typography>
                                            </Box>
                                        ) : searchedDocuments.length > 0 ? (
                                            <UnpairedSearchDocumentsTable
                                                documents={searchedDocuments}
                                                onViewDocument={
                                                    handleViewDocument
                                                }
                                                onPairDocument={
                                                    handlePairDocument
                                                }
                                            />
                                        ) : (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                padding={4}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    No unpaired documents found
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            {/* Empty State */}
            {filteredTerms.length === 0 && !isLoadingResidentTerms && (
                <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                        No resident terms found
                    </Typography>
                    <Typography variant="body2">
                        {allResidentTerms.length === 0
                            ? 'Upload a rent roll first to create resident terms'
                            : 'No results match your search or filter criteria'}
                    </Typography>
                </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Box className={classes.pagination}>
                    <TextField
                        select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        variant="outlined"
                        size="small"
                        style={{ width: 80 }}
                    >
                        {[25, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {(page - 1) * pageSize + 1} -{' '}
                        {Math.min(page * pageSize, filteredTerms.length)} of{' '}
                        {filteredTerms.length}
                    </Typography>
                </Box>
            )}

            {/* Document Viewer */}
            <DocumentViewer
                open={!!viewingDocument}
                documentId={viewingDocument?.id || null}
                documentName={viewingDocument?.name}
                documentUrl={viewingDocument?.file}
                onClose={handleViewerClose}
            />
        </Box>
    )
}
